// https://github.com/rowanwins/vue-dropzone/
<template>
  <div :id="id"
    ref="dropzoneElement"
    :class="[{ 'vue-dropzone dropzone': includeStyling},additionalClasses]">
    <div v-if="useCustomSlot"
      class="dz-message">
      <slot>Drop files here to upload</slot>
    </div>
  </div>
</template>

<script>
import Dropzone from 'dropzone' //eslint-disable-line

Dropzone.autoDiscover = false

export default {
  props: {
    id: {
      type: String,
      required: true,
      default: 'dropzone'
    },
    options: {
      type: Object,
      required: true
    },
    additionalClasses: {
      type: String,
      default: ''
    },
    includeStyling: {
      type: Boolean,
      default: true,
      required: false
    },
    destroyDropzone: {
      type: Boolean,
      default: true,
      required: false
    },
    duplicateCheck: {
      type: Boolean,
      default: false,
      required: false
    },
    useCustomSlot: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      dropzone: null
    }
  },
  computed: {
    dropzoneSettings() {
      let defaultValues = {
        thumbnailWidth: 128,
        thumbnailHeight: 128,
        previewTemplate: this.customTemplate()
      }
      Object.keys(this.options).forEach(function (key) {
        defaultValues[key] = this.options[key]
      }, this)
      return defaultValues
    }
  },
  mounted() {
    if (this.$isServer && this.hasBeenMounted) {
      return
    }
    this.hasBeenMounted = true
    this.dropzone = new Dropzone(this.$refs.dropzoneElement, this.dropzoneSettings)
    let vm = this
    this.dropzone.on('thumbnail', function (file, dataUrl) {
      vm.$emit('vdropzone-thumbnail', file, dataUrl)
    })
    this.dropzone.on('addedfile', function (file) {
      if (vm.duplicateCheck) {
        if (this.files.length) {
          var _i, _len
          for (
            _i = 0, _len = this.files.length;
            _i < _len - 1;
            _i++ // -1 to exclude current file
          ) {
            if (
              this.files[_i].name === file.name &&
              this.files[_i].size === file.size &&
              this.files[_i].lastModifiedDate.toString() === file.lastModifiedDate.toString()
            ) {
              this.removeFile(file)
              vm.$emit('vdropzone-duplicate-file', file)
            }
          }
        }
      }
      vm.$emit('vdropzone-file-added', file)
    })
    this.dropzone.on('addedfiles', function (files) {
      vm.$emit('vdropzone-files-added', files)
    })
    this.dropzone.on('removedfile', function (file) {
      vm.$emit('vdropzone-removed-file', file)
      if (file.manuallyAdded && vm.dropzone.options.maxFiles !== null) vm.dropzone.options.maxFiles++
    })
    this.dropzone.on('success', function (file, response) {
      vm.$emit('vdropzone-success', file, response)
    })
    this.dropzone.on('successmultiple', function (file, response) {
      vm.$emit('vdropzone-success-multiple', file, response)
    })
    this.dropzone.on('error', function (file, message, xhr) {
      vm.$emit('vdropzone-error', file, message, xhr)
    })
    this.dropzone.on('errormultiple', function (files, message, xhr) {
      vm.$emit('vdropzone-error-multiple', files, message, xhr)
    })
    this.dropzone.on('sending', function (file, xhr, formData) {
      vm.$emit('vdropzone-sending', file, xhr, formData)
    })
    this.dropzone.on('sendingmultiple', function (file, xhr, formData) {
      vm.$emit('vdropzone-sending-multiple', file, xhr, formData)
    })
    this.dropzone.on('complete', function (file) {
      vm.$emit('vdropzone-complete', file)
    })
    this.dropzone.on('completemultiple', function (files) {
      vm.$emit('vdropzone-complete-multiple', files)
    })
    this.dropzone.on('canceled', function (file) {
      vm.$emit('vdropzone-canceled', file)
    })
    this.dropzone.on('canceledmultiple', function (files) {
      vm.$emit('vdropzone-canceled-multiple', files)
    })
    this.dropzone.on('maxfilesreached', function (files) {
      vm.$emit('vdropzone-max-files-reached', files)
    })
    this.dropzone.on('maxfilesexceeded', function (file) {
      vm.$emit('vdropzone-max-files-exceeded', file)
    })
    this.dropzone.on('processing', function (file) {
      vm.$emit('vdropzone-processing', file)
    })
    this.dropzone.on('processingmultiple', function (files) {
      vm.$emit('vdropzone-processing-multiple', files)
    })
    this.dropzone.on('uploadprogress', function (file, progress, bytesSent) {
      vm.$emit('vdropzone-upload-progress', file, progress, bytesSent)
    })
    this.dropzone.on('totaluploadprogress', function (totaluploadprogress, totalBytes, totalBytesSent) {
      vm.$emit('vdropzone-total-upload-progress', totaluploadprogress, totalBytes, totalBytesSent)
    })
    this.dropzone.on('reset', function () {
      vm.$emit('vdropzone-reset')
    })
    this.dropzone.on('queuecomplete', function () {
      vm.$emit('vdropzone-queue-complete')
    })
    this.dropzone.on('drop', function (event) {
      vm.$emit('vdropzone-drop', event)
    })
    this.dropzone.on('dragstart', function (event) {
      vm.$emit('vdropzone-drag-start', event)
    })
    this.dropzone.on('dragend', function (event) {
      vm.$emit('vdropzone-drag-end', event)
    })
    this.dropzone.on('dragenter', function (event) {
      vm.$emit('vdropzone-drag-enter', event)
    })
    this.dropzone.on('dragover', function (event) {
      vm.$emit('vdropzone-drag-over', event)
    })
    this.dropzone.on('dragleave', function (event) {
      vm.$emit('vdropzone-drag-leave', event)
    })
    vm.$emit('vdropzone-mounted')
  },
  beforeDestroy() {
    if (this.destroyDropzone) this.dropzone.destroy()
  },
  methods: {
    manuallyAddFile: function (file, fileUrl) {
      file.manuallyAdded = true
      this.dropzone.emit('addedfile', file)
      let containsImageFileType = false
      if (fileUrl.indexOf('.svg') > -1 || fileUrl.indexOf('.png') > -1 || fileUrl.indexOf('.jpg') > -1 || fileUrl.indexOf('.jpeg') > -1)
        containsImageFileType = true
      if (this.dropzone.options.createImageThumbnails && containsImageFileType && file.size <= this.dropzone.options.maxThumbnailFilesize * 1024 * 1024) {
        fileUrl && this.dropzone.emit('thumbnail', file, fileUrl)
        var thumbnails = file.previewElement.querySelectorAll('[data-dz-thumbnail]')
        for (var i = 0; i < thumbnails.length; i++) {
          thumbnails[i].style.width = this.dropzoneSettings.thumbnailWidth + 'px'
          thumbnails[i].style.height = this.dropzoneSettings.thumbnailHeight + 'px'
          thumbnails[i].style['object-fit'] = 'contain'
        }
      }
      this.dropzone.emit('complete', file)
      if (this.dropzone.options.maxFiles) this.dropzone.options.maxFiles--
      this.dropzone.files.push(file)
      this.$emit('vdropzone-file-added-manually', file)
    },
    setOption: function (option, value) {
      this.dropzone.options[option] = value
    },
    removeAllFiles: function (bool) {
      this.dropzone.removeAllFiles(bool)
    },
    processQueue: function () {
      let dropzoneEle = this.dropzone
      this.dropzone.processQueue()
      this.dropzone.on('success', function () {
        dropzoneEle.options.autoProcessQueue = true
      })
      this.dropzone.on('queuecomplete', function () {
        dropzoneEle.options.autoProcessQueue = false
      })
    },
    init: function () {
      return this.dropzone.init()
    },
    destroy: function () {
      return this.dropzone.destroy()
    },
    updateTotalUploadProgress: function () {
      return this.dropzone.updateTotalUploadProgress()
    },
    getFallbackForm: function () {
      return this.dropzone.getFallbackForm()
    },
    getExistingFallback: function () {
      return this.dropzone.getExistingFallback()
    },
    setupEventListeners: function () {
      return this.dropzone.setupEventListeners()
    },
    removeEventListeners: function () {
      return this.dropzone.removeEventListeners()
    },
    disable: function () {
      return this.dropzone.disable()
    },
    enable: function () {
      return this.dropzone.enable()
    },
    filesize: function (size) {
      return this.dropzone.filesize(size)
    },
    accept: function (file, done) {
      return this.dropzone.accept(file, done)
    },
    addFile: function (file) {
      return this.dropzone.addFile(file)
    },
    removeFile: function (file) {
      this.dropzone.removeFile(file)
    },
    getAcceptedFiles: function () {
      return this.dropzone.getAcceptedFiles()
    },
    getRejectedFiles: function () {
      return this.dropzone.getRejectedFiles()
    },
    getFilesWithStatus: function () {
      return this.dropzone.getFilesWithStatus()
    },
    getQueuedFiles: function () {
      return this.dropzone.getQueuedFiles()
    },
    getUploadingFiles: function () {
      return this.dropzone.getUploadingFiles()
    },
    getAddedFiles: function () {
      return this.dropzone.getAddedFiles()
    },
    getActiveFiles: function () {
      return this.dropzone.getActiveFiles()
    },
    changeUrl: function (url) {
      this.dropzone.options.url = url
    },
    processFile: function (file) {
      return this.dropzone.processFile(file)
    },
    customTemplate: function () {
      return `
        <div class="dz-preview dz-file-preview">
          <div class="dz-image">
            <img data-dz-thumbnail />
          </div>
          <div class="dz-details">
            <div class="dz-file-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </div>
            <div class="dz-error-mark">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2L2 20h20L12 2z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>
            <div class="dz-filename"><span>Unsupported<br>File Type / Size</span></div>
            <div class="dz-error-message"><span data-dz-errormessage></span></div>
            <div class="dz-size" data-dz-name></div>
          </div>
          <div class="dz-success-mark">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 6L9 17l-5-5"></path>
            </svg>
          </div>
          <button class="dz-remove" type="button" data-dz-remove>×</button>
          <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
        </div>
      `
    }
  }
}
</script>

<style>
@import '../../../node_modules/dropzone/dist/dropzone.css';

.vue-dropzone {
  /* Keep dropzone.css internals (.dz-progress z 1000, .dz-success-mark z 500 etc.) from painting over sticky headers */
  isolation: isolate;
  border: 1px solid var(--vue-dropzone-border-color);
  letter-spacing: 0.2px;
  color: #777;
  transition: 0.2s linear;
  border-radius: 4px;
  background-color: var(--background-color);
}

.vue-dropzone:hover {
  background-color: var(--background-hover-color);
}

.vue-dropzone > i {
  color: #ccc;
}

.vue-dropzone .dz-preview.dz-image-preview {
  background-color: transparent;
}

/* File Preview Card Styling */
.vue-dropzone .dz-preview.dz-file-preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 6px;
  min-height: auto;
  width: 128px;
  height: 128px;
  border-radius: 16px;
  overflow: visible;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

/* Error state border */
.vue-dropzone .dz-preview.dz-error {
  border-color: #d9534d;
}

/* Success state */
.vue-dropzone .dz-preview.dz-success {
  border-color: #5cb85c;
}

.vue-dropzone .dz-preview.dz-file-preview .dz-image {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 14px;
  overflow: hidden;
}

.vue-dropzone .dz-preview.dz-file-preview .dz-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Hide image for error files */
.vue-dropzone .dz-preview.dz-error .dz-image {
  display: none;
}

.vue-dropzone .dz-preview .dz-details {
  position: relative;
  opacity: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: transparent;
  border: none;
  border-radius: 14px;
  padding: 15px 12px;
  text-align: center;
  box-shadow: none;
}

/* Hide details for successful uploads */
.vue-dropzone .dz-preview.dz-success .dz-details {
  display: none;
}

/* Show details only for processing or error states */
.vue-dropzone .dz-preview.dz-processing .dz-details,
.vue-dropzone .dz-preview.dz-error .dz-details {
  display: flex;
}

/* Show filename for successful completed uploads */
.vue-dropzone .dz-preview.dz-file-preview.dz-success.dz-complete .dz-details,
.vue-dropzone .dz-preview.dz-file-preview.dz-processing.dz-success.dz-complete .dz-details {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 10px;
  background: transparent;
  flex-direction: column;
}

.vue-dropzone .dz-preview.dz-file-preview.dz-success.dz-complete .dz-image,
.vue-dropzone .dz-preview.dz-file-preview.dz-processing.dz-success.dz-complete .dz-image {
  display: none;
}

.vue-dropzone .dz-preview.dz-file-preview.dz-success.dz-complete .dz-error-mark,
.vue-dropzone .dz-preview.dz-file-preview.dz-success.dz-complete .dz-filename,
.vue-dropzone .dz-preview.dz-file-preview.dz-processing.dz-success.dz-complete .dz-error-mark,
.vue-dropzone .dz-preview.dz-file-preview.dz-processing.dz-success.dz-complete .dz-filename {
  display: none !important;
}

/* Image Preview Error State Styling - similar to file-preview */
.vue-dropzone .dz-preview.dz-image-preview.dz-error.dz-complete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 6px;
  min-height: auto;
  width: 128px;
  height: 128px;
  border-radius: 16px;
  overflow: visible;
  border: 1px solid #d9534d;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

.vue-dropzone .dz-preview.dz-image-preview.dz-error.dz-complete .dz-image {
  display: none;
}

.vue-dropzone .dz-preview.dz-image-preview.dz-error.dz-complete .dz-details {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 15px 12px;
  background: transparent;
}

.vue-dropzone .dz-preview.dz-image-preview.dz-error.dz-complete .dz-error-mark {
  display: flex;
}

.vue-dropzone .dz-preview.dz-image-preview.dz-error.dz-complete .dz-filename {
  display: block;
}

.vue-dropzone .dz-preview.dz-image-preview.dz-error.dz-complete .dz-size {
  display: block;
}

.vue-dropzone .dz-preview.dz-image-preview.dz-error.dz-complete .dz-success-mark {
  display: none;
}

.vue-dropzone .dz-preview .dz-file-icon {
  display: none;
  width: 52px;
  height: 52px;
  color: #63c867;
  flex: 0 0 auto;
}

.vue-dropzone .dz-preview .dz-file-icon svg {
  width: 100%;
  height: 100%;
}

.vue-dropzone .dz-preview.dz-file-preview.dz-success.dz-complete .dz-file-icon,
.vue-dropzone .dz-preview.dz-file-preview.dz-processing.dz-success.dz-complete .dz-file-icon {
  display: block;
}

.vue-dropzone .dz-preview .dz-error-mark {
  position: relative;
  display: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-bottom: 6px;
}

/* Only show error mark for error state */
.vue-dropzone .dz-preview.dz-error .dz-error-mark {
  display: flex;
}

.vue-dropzone .dz-preview .dz-error-mark svg {
  width: 24px;
  height: 24px;
  stroke: #d9534d;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.vue-dropzone .dz-preview .dz-filename {
  color: #d9534d;
  font-weight: 700;
  font-size: 10px;
  line-height: 1.3;
  margin: 4px 0;
  overflow: visible;
  display: none;
}

/* Only show filename for error state */
.vue-dropzone .dz-preview.dz-error .dz-filename {
  display: block;
}

.vue-dropzone .dz-preview .dz-filename span {
  background-color: transparent;
  padding: 0;
  border: none;
  display: block;
}

.vue-dropzone .dz-preview .dz-filename span:hover {
  background-color: transparent;
  border: none !important;
  outline: none;
}
.dropzone .dz-preview .dz-details .dz-filename{
  margin: 0 !important;
}

.dropzone .dz-preview .dz-details .dz-filename:not(:hover) {
  overflow: visible !important;
  border: none;
}

.dropzone .dz-preview .dz-details .dz-filename:hover {
  border: none !important;
  outline: none;
}

.dropzone .dz-preview .dz-details .dz-filename:not(:hover) span {
    border: none;
}

.vue-dropzone .dz-preview .dz-size {
  color: #333333;
  font-size: 12px !important;
  font-weight: bold;
  margin-top: 0px;
  display: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
  position: relative;
}

.vue-dropzone .dz-preview .dz-size:hover::after {
  content: attr(data-dz-name);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: #ffffff;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: normal;
  word-wrap: break-word;
  max-width: 80px;
  z-index: 1000;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  line-height: 1.3;
}

.vue-dropzone .dz-preview .dz-size:hover::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #333;
  z-index: 1000;
  margin-bottom: -5px;
}

/* Only show size (filename) for error state */
.vue-dropzone .dz-preview.dz-error .dz-size {
  display: block;
}

.vue-dropzone .dz-preview.dz-file-preview.dz-success.dz-complete .dz-size,
.vue-dropzone .dz-preview.dz-file-preview.dz-processing.dz-success.dz-complete .dz-size {
  display: block;
  color: #333333;
  font-size: 12px !important;
  font-weight: 600;
  max-width: 104px;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 4px 8px;
  border-radius: 4px;
  margin-top: 2px;
  text-align: center;
  line-height: 1.2;
}

.vue-dropzone .dz-preview .dz-size span {
  background-color: transparent;
  padding: 0;
  border: none;
  display: inline;
}

.vue-dropzone .dz-preview .dz-progress {
  display: none;
}

.vue-dropzone .dz-preview .dz-progress .dz-upload {
  display: none;
}

.vue-dropzone .dz-preview .dz-remove {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 30;
  width: 22px;
  height: 22px;
  background-color: #ffffff;
  border: 1px solid #666666;
  border-radius: 50%;
  color: #4a4a4a;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  transition: all 0.2s ease;
  opacity: 1;
}

.vue-dropzone .dz-preview .dz-remove:hover {
  text-decoration: none;
  background-color: #f8f8f8;
  border-color: #555555;
  color: #333333;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transform: none;
}

.vue-dropzone .dz-preview .dz-error-message {
  display: none !important;
}

/* Show readable hover message for error state */
.vue-dropzone .dz-preview.dz-error:hover .dz-error-message {
  display: block !important;
  position: absolute;
  left: 50%;
  top: calc(100% + 10px);
  transform: translateX(-50%);
  width: 150px;
  max-width: 150px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #8f1d1d;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  word-break: break-word;
  z-index: 40;
  pointer-events: none;
}

.vue-dropzone .dz-preview.dz-error:hover .dz-error-message::before {
  content: '';
  position: absolute;
  left: 50%;
  top: -6px;
  width: 10px;
  height: 10px;
  transform: translateX(-50%) rotate(45deg);
}

.vue-dropzone .dz-preview.dz-error:hover .dz-error-message span {
  color: inherit;
  font-weight: inherit;
}

.vue-dropzone .dz-preview .dz-success-mark {
  display: none;
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 25;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #ffffff;
  color: #43aa47;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.22);
}

.vue-dropzone .dz-preview.dz-success.dz-complete .dz-success-mark {
  display: flex;
}

.vue-dropzone .dz-preview .dz-success-mark svg {
  width: 13px;
  height: 13px;
}

.vue-dropzone .dz-preview .dz-error-mark {
  z-index: auto;
  opacity: 1;
  position: relative;
  top: auto;
  left: auto;
  margin: 0 0 15px 0;
}

.vue-dropzone .dz-preview .dz-success-mark svg,
.vue-dropzone .dz-preview .dz-error-mark svg {
  margin: 0;
}

.vue-dropzone .dz-preview:hover .dz-details {
  opacity: 1;
}
</style>
